import React, { useEffect, useState } from 'react'
import { Button, Col, Input, Modal, Row, Typography } from 'antd'
import {
  DeleteFilled,
  ExclamationCircleFilled,
  PlusCircleOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '~/Store'
import { RolesActions } from '~/Store/Role'
import {
  getIsCreatingRole,
  getIsDeletingRole,
  getIsRenamingRole,
  getListRoles,
} from '~/Store/Role/selectors'
import { RolesResponse } from '~/Services/AdminRolesService'
import { ScrollBar } from '~/Components/ScrollBar'
import { NoData } from '~/Components/NoData'

const { confirm } = Modal

export const UserRoles = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const roles = useAppSelector(getListRoles)
  const isCreating = useAppSelector(getIsCreatingRole)
  const isDeleting = useAppSelector(getIsDeletingRole)
  const isRenaming = useAppSelector(getIsRenamingRole)

  const [createModalVisible, setCreateModalVisible] = useState(false)
  const [newRoleName, setNewRoleName] = useState('')

  const [renamingRole, setRenamingRole] = useState<RolesResponse | undefined>()
  const [renameValue, setRenameValue] = useState('')

  useEffect(() => {
    dispatch(RolesActions.getRolesRequest('User'))
  }, [])

  const newRoleClick = () => {
    setNewRoleName('')
    setCreateModalVisible(true)
  }

  const createRole = () => {
    if (!newRoleName.trim()) return
    dispatch(
      RolesActions.addRolesRequest({ roleName: newRoleName.trim(), type: 'User' }),
    )
    setCreateModalVisible(false)
  }

  const rowClick = (role: RolesResponse) => {
    setRenamingRole(role)
    setRenameValue(role.name)
  }

  const closeRenameModal = () => {
    setRenamingRole(undefined)
  }

  const renameRole = () => {
    if (!renamingRole || !renameValue.trim()) return
    dispatch(
      RolesActions.updateRoleRequest({
        roleName: renamingRole.name,
        newRolename: renameValue.trim(),
        type: 'User',
      }),
    )
    setRenamingRole(undefined)
  }

  const deleteClick = (role: RolesResponse) => {
    confirm({
      title: t('admin.roleConfirmDelete'),
      icon: <ExclamationCircleFilled />,
      content: <div>{t('admin.roleName')}: {role.name}</div>,
      okText: t('admin.delete'),
      okType: 'danger',
      cancelText: t('cancel'),
      onOk() {
        dispatch(RolesActions.deleteRoleRequest({ roleName: role.name, type: 'User' }))
      },
    })
  }

  return (
    <div>
      <Modal
        title={t('admin.roleCreateTitle')}
        visible={createModalVisible}
        onOk={createRole}
        onCancel={() => setCreateModalVisible(false)}
        confirmLoading={isCreating}
        okText={t('save')}
        cancelText={t('cancel')}
      >
        <div style={{ marginBottom: '6px' }}>{t('admin.roleName')}</div>
        <Input
          placeholder={t('admin.roleNamePlaceholder')}
          value={newRoleName}
          onChange={evt => setNewRoleName(evt.target.value)}
          onPressEnter={createRole}
        />
      </Modal>

      <Modal
        title={t('admin.roleRenameTitle')}
        visible={!!renamingRole}
        onOk={renameRole}
        onCancel={closeRenameModal}
        confirmLoading={isRenaming}
        okText={t('save')}
        cancelText={t('cancel')}
      >
        <div style={{ marginBottom: '6px' }}>{t('admin.roleName')}</div>
        <Input
          value={renameValue}
          onChange={evt => setRenameValue(evt.target.value)}
          onPressEnter={renameRole}
        />
      </Modal>

      <Row align="middle" style={{ marginBottom: '16px' }}>
        <Typography.Title style={{ flex: 1, margin: 0 }} level={4}>
          {t('admin.userRoleManagement')}
        </Typography.Title>
        <Button
          type="primary"
          icon={<PlusCircleOutlined />}
          onClick={newRoleClick}
        >
          {t('admin.roleNewRole')}
        </Button>
      </Row>

      <div className="listItemBordered" style={{ fontWeight: 'bold' }}>
        <Row gutter={[8, 8]}>
          <Col xs={24} sm={20} md={20}>
            {t('admin.roleName')}
          </Col>
          <Col xs={24} sm={4} md={4} />
        </Row>
      </div>
      <ScrollBar
        style={{
          paddingRight: '16px',
          marginRight: '-16px',
          height: 'calc(100vh - 280px)',
        }}
      >
        {roles && roles.length > 0 ? (
          roles.map(role => (
            <div
              className="listItemBordered"
              key={role.id}
              style={{ cursor: 'pointer' }}
              onClick={() => rowClick(role)}
            >
              <Row gutter={[8, 8]} align="middle">
                <Col xs={24} sm={20} md={20}>
                  {role.name}
                </Col>
                <Col xs={24} sm={4} md={4} style={{ textAlign: 'end' }}>
                  <DeleteFilled
                    style={{ color: 'red', opacity: isDeleting ? 0.5 : 1 }}
                    onClick={evt => {
                      evt.stopPropagation()
                      if (!isDeleting) deleteClick(role)
                    }}
                  />
                </Col>
              </Row>
            </div>
          ))
        ) : (
          <NoData content={t('admin.noData')} />
        )}
      </ScrollBar>
    </div>
  )
}
